import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/** Máximo de questões selecionadas de uma vez no banco (o editor recebe todas). */
export const MAX_SELECTION = 50

const STORAGE_KEY = 'selectedQuestionIds'

interface QuestionSelectionContextType {
  selectedIds: string[]
  /** Devolve false quando a seleção já está no limite. */
  select: (id: string) => boolean
  deselect: (id: string) => void
  /** Devolve false quando tentou marcar além do limite (desmarcar sempre passa). */
  toggleSelect: (id: string) => boolean
  clearSelection: () => void
  isSelected: (id: string) => boolean
  setSelection: (ids: string[]) => void
  isAtLimit: boolean
  maxSelection: number
}

const QuestionSelectionContext = createContext<QuestionSelectionContextType | undefined>(undefined)

const readStoredSelection = (): string[] => {
  if (typeof window === 'undefined') return []
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = stored ? JSON.parse(stored) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string').slice(0, MAX_SELECTION) : []
  } catch {
    return []
  }
}

export const QuestionSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(readStoredSelection)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedIds))
    } catch {
      // Sem storage (tracking prevention): a seleção vive só na sessão
    }
  }, [selectedIds])

  const isAtLimit = selectedIds.length >= MAX_SELECTION

  const select = useCallback(
    (id: string) => {
      if (selectedIds.includes(id)) return true
      if (selectedIds.length >= MAX_SELECTION) return false
      setSelectedIds(prev =>
        prev.includes(id) || prev.length >= MAX_SELECTION ? prev : [...prev, id]
      )
      return true
    },
    [selectedIds]
  )

  const deselect = useCallback((id: string) => {
    setSelectedIds(prev => prev.filter(qid => qid !== id))
  }, [])

  const toggleSelect = useCallback(
    (id: string) => {
      if (selectedIds.includes(id)) {
        deselect(id)
        return true
      }
      return select(id)
    },
    [selectedIds, select, deselect]
  )

  const clearSelection = useCallback(() => setSelectedIds([]), [])
  const isSelected = useCallback((id: string) => selectedIds.includes(id), [selectedIds])
  const setSelection = useCallback((ids: string[]) => setSelectedIds(ids.slice(0, MAX_SELECTION)), [])

  const value = useMemo<QuestionSelectionContextType>(
    () => ({
      selectedIds,
      select,
      deselect,
      toggleSelect,
      clearSelection,
      isSelected,
      setSelection,
      isAtLimit,
      maxSelection: MAX_SELECTION
    }),
    [selectedIds, select, deselect, toggleSelect, clearSelection, isSelected, setSelection, isAtLimit]
  )

  return (
    <QuestionSelectionContext.Provider value={value}>
      {children}
    </QuestionSelectionContext.Provider>
  )
}

export const useQuestionSelection = () => {
  const ctx = useContext(QuestionSelectionContext)
  if (!ctx) throw new Error('useQuestionSelection must be used within a QuestionSelectionProvider')
  return ctx
}
